import { useEffect, useCallback, useRef } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import type { GalleryPhoto } from '../types/wp'
import { useLocale } from '../i18n/LocaleContext'

interface LightboxProps {
  photos: GalleryPhoto[]
  currentIndex: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}

export default function Lightbox({ photos, currentIndex, onClose, onPrev, onNext }: LightboxProps) {
  const { locale } = useLocale()
  const photo = photos[currentIndex]
  const dialogRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') onPrev()
      if (e.key === 'ArrowRight') onNext()
      if (e.key === 'Tab') {
        const dialog = dialogRef.current
        if (!dialog) return
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) {
          e.preventDefault()
          return
        }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        const active = document.activeElement
        if (e.shiftKey) {
          if (active === first || !dialog.contains(active)) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (active === last || !dialog.contains(active)) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    },
    [onClose, onPrev, onNext]
  )

  // Focus management + scroll lock: run once while mounted so navigating
  // between photos (handleKey changes per render) doesn't thrash focus.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'
    // tabIndex={-1} makes the dialog programmatically focusable.
    dialogRef.current?.focus()
    return () => {
      document.body.style.overflow = ''
      previouslyFocused.current?.focus()
    }
  }, [])

  // (Re)subscribe the keyboard handler whenever the callbacks change.
  useEffect(() => {
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [handleKey])

  if (!photo) return null

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={locale === 'hu' ? 'Képgaléria' : 'Galerie foto'}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 focus:outline-none"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-4 right-4 z-10 text-white/80 hover:text-white transition-colors"
        onClick={onClose}
        aria-label="Close"
      >
        <X size={32} />
      </button>

      {/* Prev */}
      {photos.length > 1 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/60 hover:text-white transition-colors p-2"
          onClick={(e) => { e.stopPropagation(); onPrev() }}
          aria-label="Previous"
        >
          <ChevronLeft size={40} />
        </button>
      )}

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.src}
          alt=""
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
        />
        <p className="text-white/50 text-xs mt-3">
          {currentIndex + 1} / {photos.length}
        </p>
      </div>

      {/* Next */}
      {photos.length > 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/60 hover:text-white transition-colors p-2"
          onClick={(e) => { e.stopPropagation(); onNext() }}
          aria-label="Next"
        >
          <ChevronRight size={40} />
        </button>
      )}
    </div>
  )
}
